import { useRef, useState } from 'react';
import {
  createWritingType,
  updateWritingType,
  setWritingTypeActive,
} from 'services/escriturasTemplatesService';

/** Lee el valor de un onChange que puede venir como evento (target.value) o valor crudo. */
const readVal = (e) =>
  e && typeof e === 'object' && 'target' in e ? e.target.value : e;

/**
 * Crear / editar un tipo de escritura. mode = 'create' | 'edit'.
 * - create: Code (MAYÚSCULAS, inmutable tras crear) + Nombre + Familia (texto libre, opcional).
 * - edit: solo el Nombre (Code y Familia quedan deshabilitados).
 * El form (Guardar/Cancelar) es la confirmación de la acción; feedback success/error inline.
 */
export default function WritingTypeFormModal({
  DesignSystemComponents,
  mode,
  writingType,
  t,
  onClose,
  onSaved,
}) {
  const { Modal, Button, Input, Dropdown } = DesignSystemComponents ?? {};
  const isEdit = mode === 'edit';

  const [code, setCode] = useState(writingType?.code ?? '');
  const [displayName, setDisplayName] = useState(
    writingType?.displayName ?? ''
  );
  const [familyJuridica, setFamilyJuridica] = useState(
    writingType?.familyJuridica ?? ''
  );
  // Peso de carga del balanceador. Vacío → null (el balanceador usa el fallback). Solo dígitos.
  const [tiempos, setTiempos] = useState(
    writingType?.tiempos == null ? '' : String(writingType.tiempos)
  );
  // Estado activo/inactivo del tipo (solo edición): reemplaza la acción "desactivar" del
  // menú ⋮. Los tipos inactivos no aparecen en la lista → en la práctica solo se desactiva.
  const [isActive, setIsActive] = useState(writingType?.isActive !== false);
  const [phase, setPhase] = useState('form'); // form | saving | error
  const [error, setError] = useState(null);
  const submittingRef = useRef(false);

  const canSubmit =
    displayName.trim().length > 0 && (isEdit || code.trim().length > 0);

  // Vacío → null; si no, entero (descarta no-dígitos en el onChange).
  const tiemposPayload = tiempos.trim() === '' ? null : Number(tiempos);

  const handleSubmit = async () => {
    if (!canSubmit || submittingRef.current) return; // un solo envío por click
    submittingRef.current = true;
    setPhase('saving');
    setError(null);
    try {
      const result = isEdit
        ? await updateWritingType(writingType.id, {
            displayName: displayName.trim(),
            tiempos: tiemposPayload,
          })
        : await createWritingType({
            code: code.trim(),
            displayName: displayName.trim(),
            familyJuridica: familyJuridica.trim() || null,
            tiempos: tiemposPayload,
          });
      // Estado (edición): si cambió, aplícalo con el servicio del toggle actual
      // (setWritingTypeActive) — reemplaza la acción "desactivar" del menú ⋮.
      if (isEdit && isActive !== (writingType?.isActive !== false)) {
        await setWritingTypeActive(writingType.id, isActive);
      }
      onSaved(result, isEdit);
    } catch (err) {
      const conflict = err.response?.status === 409;
      setError(
        conflict
          ? t('tpl_type_code_conflict')
          : err.response?.data?.message || err.message || t('tpl_error_generic')
      );
      setPhase('error');
    } finally {
      submittingRef.current = false;
    }
  };

  const saving = phase === 'saving';
  const title = isEdit ? t('tpl_type_edit_title') : t('tpl_type_create_title');
  // Opciones del Dropdown de Estado (patrón CreateEditCourtModal): booleano directo.
  const activeOptions = [
    { label: t('tpl_doc_active'), value: true },
    { label: t('tpl_doc_inactive'), value: false },
  ];

  const body = (
    <div>
      {error && (
        <div className='alert alert-danger py-2' style={{ fontSize: 13 }}>
          {error}
        </div>
      )}

      {Input ? (
        <div className='d-flex flex-column gap-3'>
          <Input
            label={t('tpl_type_code')}
            value={code}
            onChange={(e) => setCode(readVal(e).toUpperCase())}
            disabled={isEdit || saving}
            placeholder='EJ_CODIGO'
            width='100%'
          />
          <Input
            label={t('tpl_type_name')}
            value={displayName}
            onChange={(e) => setDisplayName(readVal(e))}
            disabled={saving}
            width='100%'
          />
          <Input
            label={t('tpl_type_family')}
            value={familyJuridica}
            onChange={(e) => setFamilyJuridica(readVal(e))}
            disabled={isEdit || saving}
            placeholder='INMOBILIARIA, PATRIMONIAL, …'
            width='100%'
          />
          <Input
            label={t('tpl_type_tiempos')}
            type='number'
            value={tiempos}
            onChange={(e) => setTiempos(readVal(e).replace(/[^0-9]/g, ''))}
            disabled={saving}
            placeholder='5'
            width='100%'
            helperText={t('tpl_type_tiempos_helper')}
          />
          {isEdit && Dropdown && (
            <div>
              <label className='form-label'>{t('tpl_estado_label')}</label>
              <Dropdown
                options={activeOptions}
                value={
                  activeOptions.find((o) => o.value === isActive) ??
                  activeOptions[0]
                }
                onChange={(o) => setIsActive(o?.value ?? true)}
                width='100%'
              />
            </div>
          )}
        </div>
      ) : (
        <p className='font-light'>Cargando componentes…</p>
      )}

      {isEdit && (
        <p
          className='font-light mt-2'
          style={{ fontSize: 12, color: '#64748B' }}
        >
          {t('tpl_type_edit_hint')}
        </p>
      )}

      <div className='d-flex justify-content-end gap-2 mt-3'>
        {Button ? (
          <>
            <Button
              label={t('tpl_btn_cancel')}
              size='sm'
              variant='secondary'
              onClick={onClose}
              disabled={saving}
            />
            <Button
              label={saving ? t('tpl_loading') : t('tpl_type_save')}
              size='sm'
              variant='primary'
              onClick={handleSubmit}
              disabled={!canSubmit || saving}
            />
          </>
        ) : (
          <>
            <button
              type='button'
              className='btn btn-outline-secondary btn-sm'
              onClick={onClose}
              disabled={saving}
            >
              {t('tpl_btn_cancel')}
            </button>
            <button
              type='button'
              className='btn btn-primary btn-sm'
              onClick={handleSubmit}
              disabled={!canSubmit || saving}
            >
              {t('tpl_type_save')}
            </button>
          </>
        )}
      </div>
    </div>
  );

  if (!Modal) {
    return (
      <div
        role='dialog'
        style={{
          position: 'fixed',
          inset: 0,
          background: 'rgba(15,23,42,0.55)',
          zIndex: 9500,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: 32,
        }}
        onClick={onClose}
      >
        <div
          onClick={(e) => e.stopPropagation()}
          style={{
            background: '#fff',
            borderRadius: 8,
            padding: 20,
            width: 'min(480px, 92vw)',
          }}
        >
          <h5>{title}</h5>
          {body}
        </div>
      </div>
    );
  }

  return (
    <Modal
      title={title}
      show
      onClose={onClose}
      primaryBtnTxt={null}
      secondaryBtnTxt={null}
      width='480px'
    >
      {body}
    </Modal>
  );
}
